import { useEffect } from 'react';

/**
 * Enhanced keyboard navigation hook
 * Provides comprehensive keyboard shortcuts and improved navigation
 * WCAG 2.1 AA compliant keyboard navigation
 */
export function useKeyboardNavigation() {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip to main content (Alt + M or Alt + S)
      if ((e.altKey && e.key === 'm') || (e.altKey && e.key === 's')) {
        e.preventDefault();
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.focus();
          mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      // Skip to navigation (Alt + N)
      if (e.altKey && e.key === 'n') {
        e.preventDefault();
        const nav = document.querySelector('nav[role="navigation"]');
        if (nav && nav instanceof HTMLElement) {
          const firstLink = nav.querySelector('a, button');
          if (firstLink && firstLink instanceof HTMLElement) {
            firstLink.focus();
          }
        }
      }

      // Close modals/dialogs with Escape
      if (e.key === 'Escape') {
        // Close any open dropdowns or modals
        const openDialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
        openDialogs.forEach((dialog) => {
          const closeButton = dialog.querySelector('[aria-label*="close" i], [aria-label*="إغلاق"], button[aria-label*="close" i]');
          if (closeButton && closeButton instanceof HTMLElement) {
            closeButton.click();
          }
        });

        // Close any open menus
        const openMenus = document.querySelectorAll('[role="menu"][aria-expanded="true"]');
        openMenus.forEach((menu) => {
          const toggleButton = menu.previousElementSibling as HTMLElement;
          if (toggleButton && toggleButton.getAttribute('aria-expanded') === 'true') {
            toggleButton.click();
          }
        });
      }

      // Navigate with arrow keys in lists and menus
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const activeElement = document.activeElement;
        if (activeElement) {
          const role = activeElement.getAttribute('role');
          if (role === 'menuitem' || role === 'option' || activeElement.tagName === 'LI') {
            e.preventDefault();
            const items = Array.from(
              document.querySelectorAll(`[role="${role}"], li`)
            );
            const currentIndex = items.indexOf(activeElement);
            if (currentIndex !== -1) {
              const nextIndex = e.key === 'ArrowDown' 
                ? (currentIndex + 1) % items.length
                : (currentIndex - 1 + items.length) % items.length;
              const nextItem = items[nextIndex] as HTMLElement;
              if (nextItem) {
                nextItem.focus();
                // If it's a link or button, ensure it's focusable
                if (nextItem.tagName === 'A' || nextItem.tagName === 'BUTTON') {
                  nextItem.focus();
                } else {
                  const focusableChild = nextItem.querySelector('a, button, [tabindex="0"]');
                  if (focusableChild && focusableChild instanceof HTMLElement) {
                    focusableChild.focus();
                  }
                }
              }
            }
          }
        }
      }

      // Navigate with arrow keys in carousels/sliders
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const activeElement = document.activeElement;
        if (activeElement) {
          const carousel = activeElement.closest('[role="region"][aria-roledescription="carousel"]');
          if (carousel) {
            // Let the carousel component handle navigation
            // This is just a fallback
            return;
          }
        }
      }

      // Focus search with Ctrl/Cmd + K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[type="search"], input[aria-label*="search" i], input[placeholder*="search" i]');
        if (searchInput && searchInput instanceof HTMLElement) {
          searchInput.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}

